import styled from "styled-components";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import moment from "moment";
import _ from "lodash";

import { BreadcrumbsCommon, ClipLoaderCommon, StyledSavedCartPage, AddToCart } from "@components";
import { AppModuleLayout } from "@layouts";
import { PATH_BOOKING, PATH_BOOKING_DETAIL, PATH_HOME } from "@routes";
import { breakpoint, theme } from "@utilities";
import { getListBooking, AppDispatch } from "@redux";
import { IListBookings } from "@interfaces";
import { MonthSelector } from "../../Module/Calendar/MonthSelector";
import { stripTimeZone } from "src/Module/Checkout/checkoutHelpers";

export const Booking = () => {
    // page hooks
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();

    //page state
    const [bookings, setBookings] = useState<IListBookings[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    // Internal state for selected month/year
    const [selectedMonth, setSelectedMonth] = useState<number>(moment().month());
    const [selectedYear, setSelectedYear] = useState<number>(moment().year());

    const breadcrumbsModel = [
        { title: "Home", link: PATH_HOME },
        { title: "Bookings", link: PATH_BOOKING },
    ];

    useEffect(() => {
        fetchBookings();
    }, []);

    useEffect(() => {
        if (!loading && bookings.length > 0) {
            const urlParams = new URLSearchParams(location.search);
            const bookingReference = urlParams.get("bookingReference");

            if (bookingReference) {
                const booking = bookings.find((b) => b.bookingReference === bookingReference);
                if (booking && booking.products.length > 0) {
                    const firstProduct = booking.products[0];
                    const firstDetail = firstProduct.redeemers?.[0]?.bookingDetails?.[0];
                    if (firstDetail?.travelDate) {
                        const travelDate = stripTimeZone(firstDetail.travelDate);
                        setSelectedMonth(travelDate.month());
                        setSelectedYear(travelDate.year());
                    }
                }
            }
        }
    }, [loading, bookings, location.search]);

    const fetchBookings = async () => {
        try {
            const res = await dispatch(getListBooking());
            //@ts-ignore
            unwrapResult(res);
            //@ts-ignore
            setBookings(res.payload);
        } catch {
            setBookings([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (purchaseDate?: string | null): string => {
        if (!purchaseDate) return "N/A";
        return stripTimeZone(purchaseDate).local().format("dddd, D MMMM");
    };

    // Start a lodash wrapper on the raw bookings array
    const vouchersGroupedByDate = _(bookings)
        // 1) Flatten: for each booking (b)…
        .flatMap((b) =>
            //    …for each product (p) in that booking…
            b.products.flatMap(
                (p) =>
                    //      …for each redeemer (r) in that product…
                    p.redeemers?.flatMap(
                        (r) =>
                            //        …for each bookingDetail (detail) of that redeemer…
                            r.bookingDetails?.map((detail) => ({
                                //          • capture the parent booking object
                                booking: b,
                                //          • capture the product object
                                product: p,
                                //          • capture the specific detail object
                                detail,
                                //          • include bookingReference for later grouping
                                bookingReference: b.bookingReference,
                            })) || [], // if no bookingDetails, return an empty array
                    ) || [], // if no redeemers, return an empty array
            ),
        )

        // 2) Filter out any entries without a valid travelDate,
        //    and restrict to the currently-selected month/year
        .filter((entry) => {
            // skip if no travelDate
            if (!entry.detail?.travelDate) return false;

            // parse travelDate into a moment (adjusted via stripTimeZone)
            const travelDate = stripTimeZone(entry.detail.travelDate);

            // build start & end of selected month
            const startOfMonth = moment().year(selectedYear).month(selectedMonth).startOf("month");
            const endOfMonth = moment().year(selectedYear).month(selectedMonth).endOf("month");

            // keep only if travelDate falls between start and end (inclusive)
            return travelDate.isBetween(startOfMonth, endOfMonth, "day", "[]");
        })

        // 3) Group all remaining entries by a composite key:
        //    “formattedDate_tripId_bookingReference”
        .groupBy((entry) => {
            // format the date for display, e.g. “Tuesday, 1 July”
            const date = formatDate(entry.detail!.travelDate);

            // combine date + tripId + bookingReference so each
            // booking’s product on the same day stays separate
            return `${date}_${entry.product.tripId}_${entry.bookingReference}`;
        })

        // 4) Map each group of “duplicate” entries into a single summary object
        .map((entries, key) => {
            // split off the date portion (“Tuesday, 1 July_…”) into formattedDate
            const [formattedDate] = key.split("_");

            // grab the first entry in this group to pick up product/booking/detail
            const first = entries[0];

            return {
                // the human‐readable date to use as heading
                formattedDate,

                // the product metadata (tripId, name, voucherNumber, etc.)
                product: first.product,

                // the booking metadata (bookingReference, customer info, etc.)
                booking: first.booking,

                // a representative detail (start/end dates, pricing, etc.)
                detail: first.detail,

                // count how many items were in this group = number of guests
                guestCount: entries.length,
            };
        })

        // 5) Finally, regroup the summaries by the display date alone
        //    so you can iterate “per date” in your JSX
        .groupBy("formattedDate")

        // unwrap the lodash wrapper to a plain JS object
        .value();

    // Extract and sort the list of dates to render in order
    const voucherGroupDates = _.keys(vouchersGroupedByDate).sort((a, b) =>
        // push “N/A” to the bottom
        a === "N/A"
            ? 1
            : b === "N/A"
              ? -1
              : // otherwise compare actual dates (parsing with the same format)
                moment(a, "dddd, D MMMM").diff(moment(b, "dddd, D MMMM")),
    );

    return (
        <AppModuleLayout>
            {loading ? (
                <ClipLoaderCommon color={theme.colors.highlight} size={70} margin={150} />
            ) : (
                <StyledSavedCartPage>
                    <div className="wrapper">
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                lineHeight: "36px",
                            }}
                            className="breadCrumbs"
                        >
                            <BreadcrumbsCommon data={breadcrumbsModel} />
                            <MonthSelector
                                month={selectedMonth}
                                year={selectedYear}
                                onMonthYearChange={(month, year) => {
                                    setSelectedMonth(month);
                                    setSelectedYear(year);
                                }}
                            />
                        </div>
                        <div className="subWrapper">
                            <div className="listCarts">
                                <div className="heading">
                                    <p>Bookings</p>
                                </div>
                                {voucherGroupDates.length === 0 ? (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            minHeight: "350px",
                                            borderRadius: theme.borderRadius.large,
                                            margin: "40px 0",
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontWeight: 700,
                                                fontSize: "1rem",
                                                textAlign: "center",
                                                marginBottom: 12,
                                                color: "#222",
                                            }}
                                        >
                                            You don't have any bookings for this month.
                                        </div>
                                        <div
                                            style={{
                                                color: "#9e9e9e",
                                                fontSize: "1rem",
                                                marginBottom: 32,
                                                textAlign: "center",
                                            }}
                                        >
                                            You'll find here all bookings made.
                                        </div>
                                        <div style={{ width: 320, maxWidth: "100%" }}>
                                            <AddToCart
                                                color={theme.gradients.button}
                                                onClick={() => navigate(PATH_HOME)}
                                            >
                                                Explore trips
                                            </AddToCart>
                                        </div>
                                    </div>
                                ) : (
                                    voucherGroupDates.map((date, i) => (
                                        <div key={i}>
                                            <DateText>{date}</DateText>
                                            <div className="products-list">
                                                {vouchersGroupedByDate[date].map((entry) => {
                                                    const { product, booking, detail, guestCount } =
                                                        entry;
                                                    return (
                                                        <div
                                                            key={`${booking.bookingReference}-${product.tripId}-${detail.travelDate}`}
                                                        >
                                                            <NavLink
                                                                to={`${PATH_BOOKING_DETAIL}/${booking.bookingReference}/${product.tripId}`}
                                                            >
                                                                <StyledSavedCartWrapper>
                                                                    <StyledDateWrapper>
                                                                        <div className="product-container">
                                                                            <div className="productName">
                                                                                {product.name}
                                                                            </div>
                                                                            <img
                                                                                src="images/ChevronVectorRight.svg"
                                                                                alt="Right arrow"
                                                                            />
                                                                        </div>
                                                                        <StyledInfo>
                                                                            <div className="column-container">
                                                                                <span className="content">
                                                                                    Voucher Number:
                                                                                    <span className="content-data">
                                                                                        {
                                                                                            product.voucherNumber
                                                                                        }
                                                                                    </span>
                                                                                </span>
                                                                                <span className="content">
                                                                                    Start Date:
                                                                                    <span className="content-data">
                                                                                        {formatDate(
                                                                                            detail.travelDate,
                                                                                        )}
                                                                                    </span>
                                                                                </span>
                                                                            </div>
                                                                            <div className="column-container">
                                                                                <span className="content">
                                                                                    Guests:
                                                                                    <span className="content-data">
                                                                                        {guestCount}
                                                                                    </span>
                                                                                </span>
                                                                                <span className="content">
                                                                                    End Date:
                                                                                    <span className="content-data">
                                                                                        {formatDate(
                                                                                            detail.endDate,
                                                                                        )}
                                                                                    </span>
                                                                                </span>
                                                                            </div>
                                                                        </StyledInfo>
                                                                    </StyledDateWrapper>
                                                                </StyledSavedCartWrapper>
                                                            </NavLink>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </StyledSavedCartPage>
            )}
        </AppModuleLayout>
    );
};
const DateText = styled.div`
    color: #131214;
    margin-bottom: 10px;
    font-size: 16px;
    font-weight: 600;
`;

const StyledSavedCartWrapper = styled.div<{
    isList?: boolean;
}>`
    background-color: white;
    margin-bottom: 24px;
    border-radius: ${theme.borderRadius.medium};
`;

const StyledDateWrapper = styled.div`
    padding: 16px 0;
    padding-left: 20px;
    padding-right: 25px;
    display: flex;
    color: #6e7375;
    gap: 10px;
    flex-direction: column;
    border-bottom: 1px solid #e0e0e0;
    position: relative;

    .product-container {
        display: flex;
        align-items: center;
        justify-content: space-between;

    .productName {
        display: flex;  
        flex-direction : row;
        align-items: center;
        color: #131214;
        font-size: 18px;
        font-weight: bold;
        ${breakpoint.breakOnlyMobile`
            margin-left : 0px;
        `}
    }
`;

const StyledInfo = styled.div`
    display: flex;
    flex-direction: row;
    gap: 20px;
    padding: 10px 0;

    .column-container {
        display: flex;
        flex-direction: column;
        gap: 12px;
        flex: 1;

        .content {
            display: flex;
            gap: 6px;
            color: #4f4f4f;
            font-size: 14px;

            .content-data {
                font-weight: 500;
            }
        }

        ${breakpoint.breakOnlyMobile`
      flex-direction: column;
      gap: 8px;
    `}
    }

    ${breakpoint.breakOnlyMobile`
    flex-direction: column;
    gap: 12px;
  `}
`;
